import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FiCheck, FiSquare, FiCheckSquare } from 'react-icons/fi';

const BulkVanBlockModal = ({ isOpen, onClose, vans, onCreateBlocks }) => {
  const [formData, setFormData] = useState({
    reason: '',
    start_date: new Date(),
    end_date: new Date(new Date().setHours(new Date().getHours() + 2)), // Default to 2 hours from now
  });
  const [selectedVanIds, setSelectedVanIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        reason: '',
        start_date: new Date(),
        end_date: new Date(new Date().setHours(new Date().getHours() + 2)),
      });
      setSelectedVanIds([]);
      setSelectAll(false);
    }
  }, [isOpen]);

  // Update selectAll state based on selectedVanIds
  useEffect(() => {
    if (vans.length > 0) {
      const allSelected = selectedVanIds.length === vans.length;
      setSelectAll(allSelected);
    }
  }, [selectedVanIds, vans.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    setFormData(prev => ({ ...prev, [field]: date }));
  };

  const handleVanToggle = (vanId) => {
    setSelectedVanIds(prev => {
      if (prev.includes(vanId)) {
        return prev.filter(id => id !== vanId);
      } else {
        return [...prev, vanId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all
      setSelectedVanIds([]);
    } else {
      // Select all
      setSelectedVanIds(vans.map(van => van.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedVanIds.length === 0) {
      toast.error('Please select at least one van to block');
      return;
    }

    if (!formData.reason.trim()) {
      toast.error('Please provide a reason for the availability block');
      return;
    }

    if (formData.start_date >= formData.end_date) {
      toast.error('End date must be after start date');
      return;
    }

    setIsLoading(true);

    try {
      // Create blocks for all selected vans
      const blockPromises = selectedVanIds.map(vanId => {
        const blockData = {
          van_id: vanId,
          start_time: formData.start_date.toISOString(),
          end_time: formData.end_date.toISOString(),
          reason: formData.reason.trim(),
          created_by: 'admin'
        };
        return { vanId, blockData };
      });

      await onCreateBlocks(blockPromises);
      
      // Show success message
      const selectedVanNames = vans
        .filter(van => selectedVanIds.includes(van.id))
        .map(van => van.name)
        .join(', ');
      
      toast.success(`Successfully blocked ${selectedVanIds.length} van(s): ${selectedVanNames}`);
      onClose();
      
    } catch (error) {
      console.error('Error creating bulk van blocks:', error);
      toast.error('Failed to create some availability blocks. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-900 w-full max-w-2xl rounded-lg shadow-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b border-gray-800 sticky top-0 bg-gray-900 z-10">
          <h3 className="text-lg font-medium text-white">Bulk Block Van Availability</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            disabled={isLoading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Van Selection Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-400">
                Select Vans ({selectedVanIds.length} of {vans.length} selected)
              </label>
              <button
                type="button"
                onClick={handleSelectAll}
                className="flex items-center gap-2 px-3 py-1 bg-white hover:bg-gray-200 text-black text-sm rounded-md transition-colors"
                disabled={isLoading}
              >
                {selectAll ? <FiCheckSquare className="w-4 h-4" /> : <FiSquare className="w-4 h-4" />}
                {selectAll ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            
            <div className="border border-gray-700 rounded-lg max-h-60 overflow-y-auto">
              {vans.map((van, index) => (
                <div 
                  key={van.id}
                  className={`
                    flex items-center p-3 cursor-pointer transition-colors
                    ${selectedVanIds.includes(van.id) 
                      ? 'bg-white/30 border-white' 
                      : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                    }
                    ${index !== vans.length - 1 ? 'border-b border-gray-700' : ''}
                  `}
                  onClick={() => !isLoading && handleVanToggle(van.id)}
                >
                  <div className="flex items-center">
                    {selectedVanIds.includes(van.id) ? (
                      <FiCheckSquare className="w-5 h-5 text-white mr-3" />
                    ) : (
                      <FiSquare className="w-5 h-5 text-gray-400 mr-3" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center">
                      <div className="text-white font-medium">{van.name}</div>
                      <div className="text-blue-300 text-sm px-2 py-0.5 bg-blue-900/40 rounded-full">
                        {van.capacity} seats
                      </div>
                    </div>
                    <div className="text-gray-400 text-sm">{van.license_plate}</div>
                    <div className="text-gray-500 text-xs mt-1">
                      Status: <span className="text-green-400">{van.status}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Time Period Section */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-400 mb-3">Block Time Period</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Start Date & Time
                </label>
                <DatePicker
                  selected={formData.start_date}
                  onChange={(date) => handleDateChange(date, 'start_date')}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  End Date & Time
                </label>
                <DatePicker
                  selected={formData.end_date}
                  onChange={(date) => handleDateChange(date, 'end_date')}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Reason Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Reason for Block *
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white resize-none"
              placeholder="e.g., Vehicle maintenance, Staff break, Cleaning..."
              disabled={isLoading}
              required
            />
          </div>

          {/* Summary Section */}
          {selectedVanIds.length > 0 && (
            <div className="mb-6 p-4 bg-white/20 border border-white rounded-lg">
              <h4 className="text-white font-medium mb-2">Block Summary</h4>
              <div className="text-sm text-white space-y-1">
                <p><strong>Selected Vans:</strong> {selectedVanIds.length} van(s)</p>
                <p><strong>Duration:</strong> {formData.start_date.toLocaleString('en-GB', { timeZone: 'Europe/Athens' })} - {formData.end_date.toLocaleString('en-GB', { timeZone: 'Europe/Athens' })}</p>
                <p><strong>Total Blocks to Create:</strong> {selectedVanIds.length}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-600 text-gray-300 rounded-md hover:bg-gray-800 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-red-700 hover:bg-red-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || selectedVanIds.length === 0}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Blocks...
                </span>
              ) : (
                `Block ${selectedVanIds.length} Van${selectedVanIds.length === 1 ? '' : 's'}`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkVanBlockModal; 